import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Minus, Plus, RotateCw } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmtAZN } from "@/lib/api";
import { LINES } from "@/lib/games";
import { sfx } from "@/lib/sound";
import { useAuth } from "@/context/AuthContext";
import { Reel } from "./Reel";
import { WinLines } from "./WinLines";
import { CountUp, CoinRain } from "./Effects";

const SLOT_BETS = [0.2, 0.5, 1, 2, 5, 10, 20, 50, 100];
const BONUS_BETS = [0.4, 0.6, 1, 2, 4, 6, 10, 15, 20];
const r2 = (v) => Math.round(v * 100) / 100;
const randomGrid = (s) => Array.from({ length: 5 }, () => Array.from({ length: 3 }, () => s[Math.floor(Math.random() * s.length)]));

const InfoBox = ({ label, children, testid }) => (
  <div className="rounded-xl bg-black/70 border border-[#D4AF37]/30 px-2 sm:px-3 py-2 text-center min-w-[72px] sm:min-w-[90px] whitespace-nowrap">
    <p className="text-[9px] uppercase tracking-[0.2em] text-[#D4AF37]/80">{label}</p>
    <p className="font-display font-bold text-xs sm:text-base" data-testid={testid}>{children}</p>
  </div>
);

export const RealSlot = ({ game, mode }) => {
  const isBonus = game.type === "bonus";
  const bets = isBonus ? BONUS_BETS : SLOT_BETS;
  const { user, updateUser } = useAuth();
  const field = mode === "real" ? "balance" : "demo_balance";
  const [grid, setGrid] = useState(() => randomGrid(game.symbols));
  const [prev, setPrev] = useState(grid);
  const [spinId, setSpinId] = useState(0);
  const [spinning, setSpinning] = useState(false);
  const [betIdx, setBetIdx] = useState(2);
  const [result, setResult] = useState(null);
  const [fs, setFs] = useState({ left: 0, total: 0, bet: null });
  const [overlay, setOverlay] = useState(null);
  const pending = useRef(null);
  const tick = useRef(null);

  useEffect(() => {
    if (!isBonus) return;
    api.get("/bonus/state", { params: { mode } }).then(({ data }) => {
      if (data.free_spins_left > 0) setFs({ left: data.free_spins_left, total: data.fs_total, bet: data.fs_bet });
    });
  }, [isBonus, mode]);

  useEffect(() => () => clearInterval(tick.current), []);

  const highlights = useMemo(() => {
    const h = Array.from({ length: 5 }, () => new Set());
    result?.wins.forEach((w) => { for (let r = 0; r < w.count; r++) h[r].add(LINES[w.line][r]); });
    if (result?.bonus_triggered) result.grid.forEach((col, r) => col.forEach((s, row) => s === "scatter" && h[r].add(row)));
    return h;
  }, [result]);

  const spin = useCallback(async () => {
    if (spinning || overlay) return;
    setResult(null);
    setSpinning(true);
    sfx.click();
    try {
      const { data } = isBonus
        ? await api.post("/bonus/spin", { bet: bets[betIdx], mode })
        : await api.post("/games/spin", { game_id: game.id, bet: bets[betIdx], mode });
      updateUser({ [field]: r2(data[field] - data.total_win) });
      pending.current = data;
      setPrev(grid);
      setGrid(data.grid);
      setSpinId((s) => s + 1);
      tick.current = setInterval(sfx.reelTick, 70);
    } catch (e) {
      toast.error(errMsg(e));
      setSpinning(false);
    }
  }, [spinning, overlay, isBonus, bets, betIdx, mode, game.id, field, grid, updateUser]);

  const onDone = (i) => {
    sfx.reelStop();
    if (i !== 4) return;
    clearInterval(tick.current);
    const d = pending.current;
    updateUser({ balance: d.balance, demo_balance: d.demo_balance });
    setResult(d);
    setSpinning(false);
    const stake = d.fs_bet || bets[betIdx];
    if (d.total_win > 0) (d.total_win >= stake * 10 ? sfx.bigWin : sfx.win)();
    if (!isBonus) return;
    if (d.bonus_triggered) {
      sfx.bonus();
      setOverlay({ type: "bonus" });
      setFs({ left: d.free_spins_left, total: 0, bet: d.fs_bet });
      setTimeout(() => setOverlay(null), 2800);
    } else if (d.free_spin) {
      setFs({ left: d.free_spins_left, total: d.fs_total, bet: d.fs_bet });
      if (d.free_spins_left === 0) {
        sfx.bigWin();
        setOverlay({ type: "summary", amount: d.fs_total });
        setTimeout(() => setOverlay(null), 3500);
      }
    }
  };

  useEffect(() => {
    if (isBonus && fs.left > 0 && !spinning && !overlay) {
      const t = setTimeout(spin, 1400);
      return () => clearTimeout(t);
    }
  }, [isBonus, fs.left, spinning, overlay, spin]);

  const inFs = isBonus && fs.left > 0;
  const big = result && result.total_win >= (result.fs_bet || bets[betIdx]) * 10;

  return (
    <div className="slot-cabinet rounded-[28px] p-2.5 sm:p-5 relative select-none w-full min-w-0" data-testid={`slot-machine-${game.id}`}>
      <div className="relative h-20 sm:h-28 rounded-2xl overflow-hidden mb-3 border border-[#D4AF37]/40">
        <img src={game.image} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/30 to-black/80" />
        <div className="absolute inset-0 flex items-center justify-between px-4 sm:px-6">
          <h3 className="font-display font-black text-xl sm:text-3xl gold-text tracking-tight drop-shadow">{game.name}</h3>
          {inFs ? (
            <span className="rounded-full bg-[#c4112f] px-3 py-1.5 text-xs sm:text-sm font-display font-bold animate-pulse" data-testid="free-spins-badge">PULSUZ: {fs.left}/10</span>
          ) : (
            <span className="text-[10px] sm:text-xs uppercase tracking-[0.2em] text-zinc-300">5 xətt · 5x3</span>
          )}
        </div>
      </div>
      <div className="machine-lights h-1.5 rounded-full mb-3" />

      <div className="relative reel-window rounded-xl p-2">
        <div className="relative flex gap-1 sm:gap-1.5 min-w-0">
          {grid.map((col, i) => (
            <Reel key={i} index={i} final={col} prev={prev[i]} spinId={spinId} symbols={game.symbols} highlight={highlights[i]} onDone={onDone} />
          ))}
        </div>
        {result?.wins?.length > 0 && <WinLines wins={result.wins} />}
        {big && <CoinRain />}
        <AnimatePresence>
          {result?.total_win > 0 && !overlay && (
            <motion.div initial={{ scale: 0.3, opacity: 0, rotateX: 70 }} animate={{ scale: 1, opacity: 1, rotateX: 0 }} exit={{ opacity: 0 }}
              className="absolute inset-0 z-20 flex items-center justify-center pointer-events-none" data-testid="win-banner">
              <div className="px-6 py-3 rounded-2xl bg-black/85 border-2 border-[#FFD700] shadow-[0_0_50px_rgba(255,215,0,0.6)] text-center">
                <p className="text-xs uppercase tracking-[0.3em] text-zinc-300">{big ? "Böyük qələbə" : "Qələbə"}</p>
                <p className="font-display font-black text-3xl sm:text-4xl gold-text" data-testid="win-amount">+<CountUp value={result.total_win} /> ₼</p>
              </div>
            </motion.div>
          )}
          {overlay && (
            <motion.div initial={{ opacity: 0, scale: 0.5 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }}
              className="absolute inset-0 z-40 flex items-center justify-center bg-black/80 rounded-xl" data-testid={`bonus-overlay-${overlay.type}`}>
              <CoinRain count={34} />
              <div className="text-center relative z-50">
                {overlay.type === "bonus" ? (
                  <>
                    <p className="font-display font-black text-4xl sm:text-6xl gold-text tracking-tighter">BONUS!</p>
                    <p className="font-display font-bold text-xl sm:text-2xl mt-2">10 PULSUZ FIRLATMA</p>
                  </>
                ) : (
                  <>
                    <p className="text-sm uppercase tracking-[0.3em] text-zinc-300">Bonus qazancı</p>
                    <p className="font-display font-black text-4xl sm:text-6xl gold-text"><CountUp value={overlay.amount} duration={2000} /> ₼</p>
                  </>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <div className="mt-4 flex flex-wrap items-center justify-between gap-2 sm:gap-4">
        <div className="flex gap-2">
          <InfoBox label="Balans" testid={`slot-balance-${game.id}`}>{fmtAZN(user?.[field])}</InfoBox>
          <InfoBox label={inFs ? "Bonus cəmi" : "Qazanc"} testid={`slot-win-${game.id}`}>{fmtAZN(inFs ? fs.total : result?.total_win || 0)}</InfoBox>
        </div>
        <div className="flex items-center gap-2 sm:gap-3">
          <div className="flex items-center gap-1 rounded-full bg-black/70 border border-[#D4AF37]/30 p-1">
            <button disabled={spinning || inFs || betIdx === 0} onClick={() => setBetIdx((b) => b - 1)} className="rounded-full p-2 hover:bg-white/10 disabled:opacity-30 transition-colors" data-testid={`bet-decrease-${game.id}`}><Minus size={14} /></button>
            <div className="text-center min-w-[56px] whitespace-nowrap">
              <p className="text-[9px] uppercase tracking-[0.2em] text-[#D4AF37]/80">Mərc</p>
              <p className="font-display font-bold text-sm" data-testid={`bet-amount-${game.id}`}>{fmtAZN(inFs ? fs.bet : bets[betIdx])}</p>
            </div>
            <button disabled={spinning || inFs || betIdx === bets.length - 1} onClick={() => setBetIdx((b) => b + 1)} className="rounded-full p-2 hover:bg-white/10 disabled:opacity-30 transition-colors" data-testid={`bet-increase-${game.id}`}><Plus size={14} /></button>
          </div>
          <button onClick={spin} disabled={spinning || inFs || !!overlay} aria-label="Fırlat"
            className="spin-btn w-16 h-16 sm:w-20 sm:h-20 rounded-full flex items-center justify-center text-[#2a1d00]" data-testid={`spin-btn-${game.id}`}>
            <RotateCw size={30} strokeWidth={3} className={spinning ? "animate-spin" : ""} />
          </button>
        </div>
      </div>
    </div>
  );
};
